import type { Advertisement } from "../advertisement";
import type { Dispute } from "../dispute";
import type { Escrow } from "../escrow";
import type { TradeMessage } from "../message/message";
import type { PaymentMethod } from "../payment-method";
import type { User } from "../profile";
import type { Review } from "../review";
import {
  Check,
  Clock,
  ArrowRightLeft,
  AlertTriangle,
  DollarSign,
  XCircle,
} from "lucide-react";

export type IconKey =
  | "DollarSign"
  | "ArrowRightLeft"
  | "Check"
  | "AlertTriangle"
  | "XCircle"
  | "Clock";

export const iconMap: Record<IconKey, React.ElementType> = {
  DollarSign,
  ArrowRightLeft,
  Check,
  AlertTriangle,
  Clock,
  XCircle,
};

export const TradeType = {
  BUY: "BUY",
  SELL: "SELL",
} as const;

export type TradeType = (typeof TradeType)[keyof typeof TradeType];

export const TradeStatus = {
  PENDING: "PENDING",
  PAID: "PAID",
  EXPIRED: "EXPIRED",
  DISPUTED: "DISPUTED",
  CANCELLED: "CANCELLED",
  REFUNDED: "REFUNDED",
  UNDER_REVIEW: "UNDER_REVIEW",
  RELEASED: "RELEASED",
  AUTO_RELEASED: "AUTO_RELEASED",
} as const;

export type TradeStatus = (typeof TradeStatus)[keyof typeof TradeStatus];

export interface PaymentSnapshot {
  accountName: string;
  accountNumber: string;
  bankCode: string;
  bankName: string | null;
  country: string;
  currency: string;
}

type Percentage = number; // could later constrain or brand
type CurrencyAmount = number;

export interface TradeStats {
  activeTradesCount: number;
  disputedTradesCount: number;
  cancelledTradesCount: number;
  expiredTradesCount: number;
  completedToday: {
    count: number;
    changePercent: Percentage;
  };
  volume24h: {
    total: CurrencyAmount;
    changePercent: Percentage;
  };
}

export interface TradeMeta {
  hasNextPage: boolean;
  hasPrevPage: boolean;
  limit: number;
  page: number;
  total: number;
  totalPages: number;
}

export interface StatCard {
  change?: number;
  icon: IconKey;
  label: string;
  positive?: boolean;
  value: string | number;
}

export interface Trade {
  id: string;
  advertisementId: string;
  advertisement?: Advertisement | null;
  buyerId: string;
  sellerId: string;
  buyer?: User | null;
  seller?: User | null;
  amount: string;
  price: string;
  fiatAmount: string;
  status: TradeStatus;
  paymentMethodId?: string | null;
  paymentMethod?: PaymentMethod | null;
  paymentSnapshot?: PaymentSnapshot | null;
  escrow?: Escrow | null;
  expiresAt?: string | null;
  messages?: TradeMessage[];
  reviews?: Review[];
  dispute?: Dispute | null;
  createdAt: string;
  updatedAt: string;
  completedAt?: string | null;
  cancelledAt?: string | null;
  releasedAt?: string | null;
  escrowLocked: boolean;
  escrowReleased: boolean;
  chatClosed: boolean;
  version: number;
  ipAddress?: string | null;
  deviceFingerprint?: string | null;
}

export interface TradeInterface {
  data: Trade[];
  meta: TradeMeta;
  stats?: StatCard[];
  volumeData?: {
    date: string;
    volume: number;
    count: number;
  }[];
}
